import React, { useState, useMemo } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  List, 
  ListItem, 
  ListItemText, 
  ListItemAvatar,
  Avatar, 
  TextField, 
  Button, 
  IconButton,
  Typography, 
  Stack,
  Divider,
  Chip
} from '@mui/material';
import { Search, Forward, Close } from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const MyMessagesDialog = ({ messagesOpen, setMessagesOpen, messages }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMessages, setSelectedMessages] = useState(new Set());

  const filteredMessages = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return messages.filter(msg =>
      msg.text.toLowerCase().includes(query) ||
      msg.sender_name.toLowerCase().includes(query)
    );
  }, [messages, searchQuery]);

  const handleForwardToggle = (messageId) => {
    setSelectedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  };

  const handleForwardMessages = () => {
   
    setSelectedMessages(new Set());
  };

  return (
    <Dialog 
      open={messagesOpen} 
      onClose={() => setMessagesOpen(false)} 
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', height: '80vh', bgcolor: 'background.paper' } }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        py: 2,
        background: 'linear-gradient(45deg,rgb(23, 129, 124) 0%,rgb(146, 226, 203) 100%)'
      }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            Scraped Messages
          </Typography>
          {selectedMessages.size > 0 && (
            <Chip 
              label={`${selectedMessages.size} selected`} 
              color="primary" 
              size="small"
            />
          )}
        </Stack>
        
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search messages..."
          InputProps={{
            startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />,
            sx: { borderRadius: 28 }
          }}
          sx={{ width: 300 }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </DialogTitle>

      <DialogContent sx={{ py: 0 }}>
        <List sx={{ py: 0 }}>
          {filteredMessages.map((msg, index) => (
            <React.Fragment key={msg.message_id}>
              <ListItem 
                alignItems="flex-start"
                sx={{
                  py: 2,
                  bgcolor: selectedMessages.has(msg.message_id) ? 'action.hover' : 'inherit',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
                secondaryAction={
                  <IconButton
                    edge="end"
                    onClick={() => handleForwardToggle(msg)}
                    color={selectedMessages.has(msg) ? 'primary' : 'default'}
                  >
                    <Forward />
                  </IconButton>
                }
              >
                <ListItemAvatar>
                  <Avatar 
                    sx={{ 
                      bgcolor: 'primary.main',
                      boxShadow: 1,
                      width: 40, 
                      height: 40 
                    }}
                  >
                    {msg.sender_name.toUpperCase().charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle1" fontWeight={600}>
                        {msg.sender_name}
                        <Typography 
                          component="span" 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          @{msg.sender_id}
                        </Typography>
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {msg.media && (
                          <Chip
                            label="Media"
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {formatDistanceToNow(new Date(msg.date), { addSuffix: true })}
                        </Typography>
                      </Stack>
                    </Stack>
                  }
                  secondary={
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        mt: 0.5
                      }}
                    >
                      {msg.text}
                    </Typography>
                  }
                />
              </ListItem>
              {index < filteredMessages.length - 1 && <Divider variant="inset" />}
            </React.Fragment>
          ))}
        </List>
      </DialogContent>

      <DialogActions sx={{ 
        borderTop: '1px solid',
        borderColor: 'divider',
        py: 1.5,
        px: 3
      }}>
        <Stack direction="row" spacing={2} width="100%" justifyContent="space-between">
          <Button
            onClick={handleForwardMessages}
            variant="contained"
            disabled={selectedMessages.size === 0}
            startIcon={<Forward />}
            sx={{ 
              borderRadius: 24,
              textTransform: 'none',
              px: 3,
              boxShadow: 1
            }}
          >
            Forward Selected ({selectedMessages.size})
          </Button>
          <Button 
            onClick={() => setMessagesOpen(false)}
            variant="outlined"
            startIcon={<Close />}
            sx={{ 
              borderRadius: 24,
              textTransform: 'none',
              px: 3
            }}
          >
            Close
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default MyMessagesDialog;